import { UserManager, WebStorageStateStore, User } from 'oidc-client';
import store from '../store';
import { IAppConfig } from '@/model/IAppConfig';
import { includes } from 'lodash';

const EMAIL_VERIFIED_PROP = 'https://mijn.philipspensioenfonds-hypotheek.nl/email_verified';
class AuthService {
  private userManagerPromise!: Promise<UserManager> | undefined;

  constructor() {
    this.getUserManager();
  }

  private getUserManager(): Promise<UserManager> {
    return (this.userManagerPromise =
      this.userManagerPromise ||
      store.dispatch('config/getConfig').then((config: IAppConfig) => {
        return this.createUserManager(config.authBaseUri, config.authClientId);
      }));
  }

  private createUserManager(authBaseUri: string, authClientId: string): UserManager {
    const baseUri = document.head.baseURI;

    const postLogoutRedirectUri = `${baseUri}sessie-afgesloten`;
    /* eslint-disable @typescript-eslint/camelcase */
    const settings: any = {
      monitorSession: false,
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      authority: authBaseUri,
      client_id: authClientId,
      redirect_uri: `${baseUri}signin-oidc.html`,
      response_type: 'id_token token',
      extraQueryParams: { audience: 'ppf-api' },
      scope: 'openid profile email customers:api employees:api',
      post_logout_redirect_uri: postLogoutRedirectUri,
      filterProtocolClaims: true,
      automaticSilentRenew: true,
      silent_redirect_uri: `${baseUri}silent-refresh-oidc.html`,
      // Sadly, for Auth0 we need to do a lot of workarounds
      // since they are not exactly OIDC conformant
      metadata: {
        issuer: authBaseUri + '/',
        authorization_endpoint: authBaseUri + '/authorize',
        userinfo_endpoint: authBaseUri + '/userinfo',
        end_session_endpoint:
          authBaseUri + `/v2/logout?returnTo=${encodeURIComponent(postLogoutRedirectUri)}&client_id=${authClientId}`,
        jwks_uri: authBaseUri + '/.well-known/jwks.json'
      }
      // this part is only needed when the IDP does not provide a
      // logout endpoint in the .well-known/openid-configuration
      // - Identity Server 4 does provide the end_session_endpoint
      // - Auth0 DOES NOT provide the end_session_endpoint
      /*
          ,
          metadata: {
              issuer: AUTH0_DOMAIN + "/",
              authorization_endpoint: AUTH0_DOMAIN + "/authorize",
              userinfo_endpoint: AUTH0_DOMAIN + "/userinfo",
              end_session_endpoint: AUTH0_DOMAIN + "/v2/logout",
              jwks_uri: AUTH0_DOMAIN + "/.well-known/jwks.json",
          }
*/
    };

    return new UserManager(settings);
  }

  public isAuthenticated(): Promise<boolean> {
    return this.getUser().then(user => {
      return user !== null && !user.expired;
    });
  }

  public isEmailVerified(): Promise<boolean> {
    return this.getUser().then(user => {
      return !!user && !!user.profile && user.profile[EMAIL_VERIFIED_PROP];
    });
  }

  public isAdmin(): Promise<boolean> {
    return this.getUser().then(user => {
      return !!user && includes(user.scopes, 'employees:api');
    });
  }

  public isCustomer(): Promise<boolean> {
    return this.getUser().then(user => {
      return !!user && includes(user.scopes, 'customers:api');
    });
  }

  public getUser(): Promise<User | null> {
    return this.getUserManager().then(userManager => {
      return userManager.getUser();
    });
  }

  public getAccessToken(): Promise<string> {
    return this.getUser().then((data: any) => {
      return data.access_token;
    });
  }

  public login(args?: any): Promise<void> {
    return this.getUserManager().then(userManager => {
      return userManager.signinRedirect(args);
    });
  }

  public logout(): Promise<void> {
    return this.getUserManager().then(userManager => {
      return userManager.signoutRedirect();
    });
  }
}

export const authService = new AuthService();
